import { createSelector } from '@ngrx/store';

import { LineItem } from 'src/app/models/line-item.model';
import { Order, OrderStatus } from 'src/app/models/order.model';
import * as fromFeature from '../reducers';
import * as fromOrders from '../reducers/orders.reducer';
import * as fromRouters from '../reducers/router.reducer';

export const getOrderState = createSelector(
  fromFeature.getFeatureOrdersState,
  (state: fromFeature.OrdersState) => state.orders,
);

export const getOrdersEntities = createSelector(
  getOrderState,
  fromOrders.getOrdersEntities,
);
export const getTotalOpenOrders = createSelector(
  getOrderState,
  fromOrders.getTotalOpenOrders,
);

export const getAllOrders = createSelector(getOrdersEntities, (entities) =>
  Object.keys(entities).map((id) => entities[id]),
);

export const getOrdersLoaded = createSelector(
  getOrderState,
  fromOrders.getOrdersLoaded,
);

export const getOrdersLoading = createSelector(
  getOrderState,
  fromOrders.getOrdersLoading,
);

// ROUTER SELECTORS
export const getCurrentRouterOrder = createSelector(
  getAllOrders,
  fromRouters.getRouterFeatureState,
  (orders, router): Order => {
    if (router.state) {
      const order = orders.find((x) => x.Id === router.state.params.orderId);
      return order;
    }
  },
);

export const getCurrentRouterLineItem = createSelector(
  getAllOrders,
  fromRouters.getRouterFeatureState,
  (orders, router): LineItem => {
    if (router.state) {
      const order = orders.find((x) => x.Id === router.state.params.orderId);
      if (order) {
        if (order.Customer_Order_OLI__r) {
          const lineItem = order.Customer_Order_OLI__r.records.find(
            (x) => x.Id === router.state.params.lineItemId,
          );
          return lineItem;
        }
      }
    }
  },
);

// ORDER DETAIL DISPLAY FROM LIST SELECTORS
export const getOrderDetailView = createSelector(
  getOrderState,
  fromOrders.getOrderDetailView,
);

export const getSelectedOrderId = createSelector(
  getOrderState,
  fromOrders.getOrderSelected,
);

export const getSelectedOrder = createSelector(
  getOrdersEntities,
  getSelectedOrderId,
  (entities, orderId): Order => entities[orderId],
);

// GET SELECTED
export const getSelectedLineItemId = createSelector(
  getOrderState,
  fromOrders.getOrderLineItemSelected,
);

export const getSelectedLineItem = createSelector(
  getOrdersEntities,
  getSelectedOrderId,
  getSelectedLineItemId,
  (entities, orderId, lineItemId): LineItem => {
    const records = entities[orderId]?.Customer_Order_OLI__r?.records;
    return records?.find((x) => x.Id === lineItemId) ?? null;
  },
);

export function getActiveOlis(orders: Order[]): LineItem[] {
  return orders
    .filter((order) => order.Customer_Order_OLI__r)
    .map((o) => o.Customer_Order_OLI__r.records)
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter(
      (oli) =>
        oli.Service_Order2__r &&
        oli.Service__r?.Status__c &&
        (oli.Service_Order2__r.Status__c === OrderStatus.Ready ||
          oli.Service_Order2__r.Status__c === OrderStatus.Pending ||
          oli.Service_Order2__r.Status__c === OrderStatus.InProgress) &&
        oli.Service__r.Status__c !== OrderStatus.Canceled,
    );
}

export function getActiveOlisOrder(orders: Order[]): Order[] {
  const activeLineItems = orders
    .filter((order) => order.Customer_Order_OLI__r)
    .map((o) => o.Customer_Order_OLI__r.records)
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter(
      (oli) =>
        oli.Service_Order2__r &&
        oli.Service__r?.Status__c &&
        (oli.Service_Order2__r.Status__c === OrderStatus.Ready ||
          oli.Service_Order2__r.Status__c === OrderStatus.Pending ||
          oli.Service_Order2__r.Status__c === OrderStatus.InProgress) &&
        oli.Service__r.Status__c !== OrderStatus.Canceled,
    );

  return activeLineItems.map((oli) => oli.Service_Order2__r);
}

function getOlisOrderList(orders) {
  return orders
    .filter((order) => order.Customer_Order_OLI__r)
    .map((o) => {
      const newoliList = [];
      const oliList = o.Customer_Order_OLI__r.records;
      oliList.forEach((oli) => {
        newoliList.push({ ...oli, deal: o.Name, embeddedOrder: o });
      });

      return newoliList;
    })
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter((oli) => oli.Service_Order2__r?.Status__c?.trim().length > 0);
}

export const getActiveOrders = createSelector(
  getAllOrders,
  (orders): Order[] => {
    const tempOrders = getActiveOlis(orders).map(
      (oli) => oli.Service_Order2__r,
    );
    return tempOrders;
  },
);

// TODO: Temp function for changing orders table
export const getActiveOrdersOrderTable = createSelector(
  getAllOrders,
  (orders): Order[] => getOlisOrderList(orders),
);

export const getTotalActiveLocations = createSelector(
  getAllOrders,
  (orders): number => {
    const locations = getActiveOlis(orders).map((oli) => oli.Location__c);
    const uniqueLocations = new Set(locations);

    return uniqueLocations.size;
  },
);
